import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap'
import { Typeahead } from 'react-bootstrap-typeahead'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Cookies from 'js-cookie'
import axios from 'axios'
import html2pdf from 'html2pdf.js'
import { FaArrowLeft, FaDownload, FaPrint } from 'react-icons/fa'
import { BsArrowClockwise, BsFunnelFill, BsSearch } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'

import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints'
import { NO_IMAGE_SRC, resolveInstituteLogoSrc, getInstituteLogoPathFromRecord } from '../utils/instituteBranding'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import { useInstitute } from './commonComponents/InstituteContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import AdmitCardPrintView from './AdmitCardPrintView'
import AdmitCardShriHit1 from '../AdmitCardShriHit1'
import {
  normalizeId,
  getFilteredSubjects,
  getSubjectRows,
  buildExamRows,
  mapSubjectSchedulesToAdmitRows,
} from './admitCardHelpers'

/** Matches API check for Shri Hit admit card template */
const isShriHitAdmitCardCode = (code) => {
  const normalized = String(code || '').toUpperCase()
  return normalized === 'SHRIHITADMITCARD1'
}

const hasShriHitAdmitCardTemplate = (templates) => {
  if (!Array.isArray(templates)) return false
  return templates.some(
    (item) =>
      String(item?.template_type || '').toLowerCase() === 'admit_card' &&
      isShriHitAdmitCardCode(item?.template_code),
  )
}

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

const fetchImageAsDataUrlForPdf = async (url) => {
  if (!url || url.startsWith('data:')) return url
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (response.ok) return await blobToDataURL(await response.blob())
  } catch (error) {
    console.warn('Direct logo fetch failed for PDF:', error)
  }
  try {
    const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`
    const response = await fetch(proxyUrl)
    if (response.ok) return await blobToDataURL(await response.blob())
  } catch (error) {
    console.warn('Proxy logo fetch failed for PDF:', error)
  }
  return url
}

export default function ExamSheetAdmitcard() {
  const { token, logout, user } = useAuth()
  const authToken = token || user?.token
  const { isCollapsed } = useSidebar()
  const {
    name: instituteNameFromContext,
    logo: instituteLogoFromContext,
    id: instituteIdFromContext,
  } = useInstitute()
  const navigate = useNavigate()
  const location = useLocation()
  const { student_id: studentIdFromRoute, exam_id: examIdFromRoute } = useParams()
  const institute_id = instituteIdFromContext || Cookies.get('institute_id')
  const normalizedUserType = (user?.user_type || '').toLowerCase()
  const normalizedRoles = (user?.roles || []).map((role) => String(role).toLowerCase())
  const isAdminUser = normalizedUserType === 'admin' || normalizedRoles.includes('admin')

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [fetchedInstituteLogo, setFetchedInstituteLogo] = useState('')
  const [showFilterCard, setShowFilterCard] = useState(true)

  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [students, setStudents] = useState([])
  const [selectedCourse, setSelectedCourse] = useState('')
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedExam, setSelectedExam] = useState('')
  const [selectedStudent, setSelectedStudent] = useState('')
  const [selectedStudentName, setSelectedStudentName] = useState('')

  const [student, setStudent] = useState(null)
  const [subjects, setSubjects] = useState([])
  const [exams, setExams] = useState([])
  const [subjectSchedules, setSubjectSchedules] = useState([])
  const [examLocation, setExamLocation] = useState('')
  const [loadingCard, setLoadingCard] = useState(false)
  const [loadingSchedules, setLoadingSchedules] = useState(false)
  const [errorCard, setErrorCard] = useState('')
  const [pdfInstituteLogo, setPdfInstituteLogo] = useState('')
  const [useShriHitTemplate, setUseShriHitTemplate] = useState(false)
  const admitCardRef = useRef(null)

  useEffect(() => {
    let cancelled = false

    const fetchTemplateCodes = async () => {
      const instituteId = Number(institute_id)
      const token = authToken

      if (!Number.isFinite(instituteId) || instituteId <= 0 || !token) {
        if (!cancelled) {
          setUseShriHitTemplate(false)
        }
        return
      }

      try {
        const response = await axios.get(`${BASE_URL}/templates/codes`, {
          params: { template_type: 'admit_card' },
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            'Institute-Id': instituteId,
          },
        })

        const templates = Array.isArray(response.data?.data)
          ? response.data.data
          : []

        if (!cancelled) {
          setUseShriHitTemplate(hasShriHitAdmitCardTemplate(templates))
        }
      } catch {
        if (!cancelled) {
          setUseShriHitTemplate(false)
        }
      }
    }

    fetchTemplateCodes()
    return () => {
      cancelled = true
    }
  }, [authToken, institute_id])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    ApiEndpoints.getAllCourses().then((res) => setCourses(res.data.data || []))
  }, [])

  const examHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${authToken}`,
      'institute-Id': Number(institute_id),
    }),
    [authToken, institute_id],
  )

  useEffect(() => {
    if (!selectedCourse || !authToken) {
      setExams([])
      return
    }
    let cancelled = false
    axios
      .get(`${BASE_URL}/get/exam/all`, { headers: examHeaders })
      .then((res) => {
        if (!cancelled) setExams(Array.isArray(res.data?.data) ? res.data.data : [])
      })
      .catch(() => {
        if (!cancelled) setExams([])
      })
    return () => {
      cancelled = true
    }
  }, [selectedCourse, authToken, examHeaders])

  const instituteName =
    instituteNameFromContext || Cookies.get('name') || Cookies.get('institute_name') || ''

  const instituteDisplayLogo = useMemo(() => {
    const toHttps = (url) =>
      typeof url === 'string' ? url.replace(/^http:\/\//i, 'https://') : url

    if (instituteLogoFromContext && instituteLogoFromContext !== NO_IMAGE_SRC) {
      return toHttps(instituteLogoFromContext)
    }
    const cookieLogo = Cookies.get('institute_logo')
    if (cookieLogo) {
      const cookieResolved = resolveInstituteLogoSrc(cookieLogo)
      if (cookieResolved && cookieResolved !== NO_IMAGE_SRC) return toHttps(cookieResolved)
    }
    if (fetchedInstituteLogo) {
      const fetchedResolved = resolveInstituteLogoSrc(fetchedInstituteLogo)
      if (fetchedResolved && fetchedResolved !== NO_IMAGE_SRC) return toHttps(fetchedResolved)
    }
    return NO_IMAGE_SRC
  }, [instituteLogoFromContext, fetchedInstituteLogo])

  const headerLogoSrc =
    instituteDisplayLogo && instituteDisplayLogo !== NO_IMAGE_SRC
      ? instituteDisplayLogo
      : `${process.env.PUBLIC_URL || ''}/logo192.png`
  // Prefer data-URL for print/PDF (CORS-safe); keep remote URL for on-screen header
  const instituteLogoSrc = pdfInstituteLogo || headerLogoSrc
  const sessionName = Cookies.get('session_name') || ''

  useEffect(() => {
    setLogoError(false)
  }, [headerLogoSrc])

  useEffect(() => {
    if (instituteDisplayLogo && instituteDisplayLogo !== NO_IMAGE_SRC) return
    if (!institute_id) return

    let cancelled = false
    ;(async () => {
      try {
        const res = await ApiEndpoints.getAllInstitutesAssinged()
        const list = Array.isArray(res?.data?.data) ? res.data.data : []
        const matched = list.find(
          (row) => String(row?.institute_id ?? row?.id ?? '') === String(institute_id),
        )
        const logoPath = getInstituteLogoPathFromRecord(matched)
        if (!cancelled && logoPath) {
          const resolved = resolveInstituteLogoSrc(logoPath)
          setFetchedInstituteLogo(resolved)
          if (resolved && resolved !== NO_IMAGE_SRC) {
            Cookies.set('institute_logo', resolved, { expires: 7 })
          }
        }
      } catch {
        // keep placeholder branding
      }
    })()

    return () => {
      cancelled = true
    }
  }, [institute_id, instituteDisplayLogo])

  useEffect(() => {
    let isMounted = true
    const prepareLogoForPdf = async () => {
      if (!instituteDisplayLogo || instituteDisplayLogo === NO_IMAGE_SRC) {
        if (isMounted) setPdfInstituteLogo('')
        return
      }
      const resolved = await fetchImageAsDataUrlForPdf(instituteDisplayLogo)
      if (isMounted && resolved) setPdfInstituteLogo(resolved)
    }
    prepareLogoForPdf()
    return () => {
      isMounted = false
    }
  }, [instituteDisplayLogo])

  const filterCourseId = normalizeId(selectedCourse)
  const filterClassId = normalizeId(selectedClass)

  const courseName =
    courses.find((c) => String(c.id) === String(selectedCourse))?.name ||
    (filterCourseId ? `Course ${filterCourseId}` : '')
  const className =
    classes.find((c) => String(c.id) === String(selectedClass))?.class_name ||
    (filterClassId ? `Class ${filterClassId}` : '')

  const filteredSubjects = useMemo(
    () => getFilteredSubjects(subjects, filterCourseId, filterClassId),
    [subjects, filterCourseId, filterClassId],
  )
  const subjectRows = useMemo(() => getSubjectRows(filteredSubjects), [filteredSubjects])

  const examOptions = useMemo(() => {
    if (!Array.isArray(exams) || !filterCourseId) return []
    const norm = (v) => String(v ?? '').trim().toLowerCase()
    return exams.filter((ex) => {
      const c = norm(ex?.course_id ?? ex?.courseId ?? ex?.course?.id)
      if (c !== norm(filterCourseId)) return false
      const exClass = norm(
        ex?.class_id ?? ex?.classId ?? ex?.std_id ?? ex?.standard_id ?? ex?.class?.id,
      )
      if (!filterClassId) return true
      if (!exClass) return true
      return exClass === norm(filterClassId)
    })
  }, [exams, filterCourseId, filterClassId])

  const selectedExamOption = useMemo(
    () => examOptions.find((ex) => String(ex.id) === String(selectedExam)) || null,
    [examOptions, selectedExam],
  )

  const examName = selectedExamOption?.exam_name || ''

  const resolvedExamLocation = useMemo(() => {
    if (examLocation) return examLocation
    return selectedExamOption?.exam_location || ''
  }, [examLocation, selectedExamOption])

  const examRows = useMemo(() => {
    if (selectedExam && !loadingSchedules && subjectSchedules.length > 0) {
      return mapSubjectSchedulesToAdmitRows(subjectSchedules)
    }
    return buildExamRows(subjectRows, exams, filterCourseId, filterClassId)
  }, [
    selectedExam,
    loadingSchedules,
    subjectSchedules,
    subjectRows,
    exams,
    filterCourseId,
    filterClassId,
  ])

  useEffect(() => {
    if (!selectedCourse || !selectedClass || !authToken) {
      setSubjects([])
      return
    }
    let cancelled = false
    ApiEndpoints.getAllSubjects()
      .then((subjectRes) => {
        if (!cancelled) {
          setSubjects(Array.isArray(subjectRes?.data?.data) ? subjectRes.data.data : [])
        }
      })
      .catch(() => {
        if (!cancelled) setSubjects([])
      })
    return () => {
      cancelled = true
    }
  }, [selectedCourse, selectedClass, authToken])

  useEffect(() => {
    if (!selectedStudent || !selectedCourse || !selectedClass || !authToken) {
      setStudent(null)
      setErrorCard('')
      return
    }

    const load = async () => {
      setLoadingCard(true)
      setErrorCard('')
      try {
        const studentRes = await ApiEndpoints.viewProfile(selectedStudent)
        setStudent(studentRes?.data?.data || null)
      } catch (err) {
        setStudent(null)
        setErrorCard(err?.response?.data?.message || 'Failed to load student profile.')
      } finally {
        setLoadingCard(false)
      }
    }

    load()
  }, [selectedStudent, selectedCourse, selectedClass, authToken])

  useEffect(() => {
    if (!selectedExam || !selectedCourse || !authToken) {
      setSubjectSchedules([])
      setExamLocation('')
      return
    }
    let cancelled = false
    const loadSchedules = async () => {
      setLoadingSchedules(true)
      try {
        const schedRes = await axios.get(`${BASE_URL}/exam/subject-schedules`, {
          headers: examHeaders,
          params: { exam_id: Number(selectedExam) },
        })
        if (cancelled) return
        const raw = schedRes.data?.data ?? schedRes.data ?? []
        setSubjectSchedules(Array.isArray(raw) ? raw : [])
        setExamLocation(schedRes.data?.exam_location || '')
      } catch {
        if (!cancelled) {
          setSubjectSchedules([])
          setExamLocation('')
        }
      } finally {
        if (!cancelled) setLoadingSchedules(false)
      }
    }
    loadSchedules()
    return () => {
      cancelled = true
    }
  }, [selectedExam, selectedCourse, authToken, examHeaders])

  const handleCourseChange = (courseId) => {
    setSelectedCourse(courseId || '')
    setSelectedClass('')
    setSelectedExam('')
    setSelectedStudent('')
    setSelectedStudentName('')
    setClasses([])
    setStudents([])
    if (courseId) {
      ApiEndpoints.getAllClassesByCourseId(courseId).then((res) => {
        setClasses(res.data.data || [])
      })
    }
  }

  const handleClassChange = (classId) => {
    setSelectedClass(classId || '')
    setSelectedExam('')
    setSelectedStudent('')
    setSelectedStudentName('')
    setStudents([])
    if (classId) {
      const headers = {
        'institute-Id': institute_id || Cookies.get('institute_id'),
        Authorization: `Bearer ${token}`,
      }
      axios
        .get(BASE_URL + `/get/student/by-class?class_id=${classId}`, { headers })
        .then((res) => setStudents(res.data.data || []))
        .catch(() => setStudents([]))
    }
  }

  const handleExamChange = (examId) => {
    setSelectedExam(examId ? String(examId) : '')
    if (!examId) {
      setSelectedStudent('')
      setSelectedStudentName('')
      setStudent(null)
    }
  }

  const handleStudentChange = (studentId) => {
    setSelectedStudent(studentId || '')
    if (studentId) {
      const name = students.find((st) => String(st.id) === String(studentId))?.name || ''
      setSelectedStudentName(name)
    } else {
      setSelectedStudentName('')
      setStudent(null)
    }
  }

  const handleRefresh = useCallback(() => {
    setSelectedCourse('')
    setSelectedClass('')
    setSelectedExam('')
    setSelectedStudent('')
    setSelectedStudentName('')
    setClasses([])
    setStudents([])
    setStudent(null)
    setSubjects([])
    setExams([])
    setSubjectSchedules([])
    setErrorCard('')
  }, [])

  const isBaseAdmitcardPath =
    location.pathname === '/ExamSheet/Admitcard' || location.pathname === '/ExamSheet/Admitcard/'

  useEffect(() => {
    if (isBaseAdmitcardPath) {
      handleRefresh()
    }
  }, [location.pathname, location.key, isBaseAdmitcardPath, handleRefresh])

  useEffect(() => {
    if (isBaseAdmitcardPath) return
    if (!studentIdFromRoute || !authToken || courses.length === 0) return

    let cancelled = false

    const applyRouteParams = async () => {
      try {
        setErrorCard('')
        const res = await ApiEndpoints.viewProfile(studentIdFromRoute)
        const profile = res?.data?.data
        if (!profile || cancelled) return

        const courseId =
          profile.course_id ?? profile.courseId ?? profile.course?.id ?? profile.course?.course_id
        const classId =
          profile.class_id ??
          profile.classId ??
          profile.class?.id ??
          profile.std_id ??
          profile.standard_id
        if (!courseId || !classId) {
          setErrorCard('Student profile has no course/class; select them manually.')
          return
        }

        setSelectedCourse(String(courseId))
        const clsRes = await ApiEndpoints.getAllClassesByCourseId(courseId)
        if (cancelled) return
        setClasses(clsRes.data?.data || [])

        setSelectedClass(String(classId))

        const headers = {
          'institute-Id': institute_id || Cookies.get('institute_id'),
          Authorization: `Bearer ${token}`,
        }
        const stuRes = await axios.get(
          `${BASE_URL}/get/student/by-class?class_id=${encodeURIComponent(String(classId))}`,
          { headers },
        )
        if (cancelled) return
        const list = stuRes.data?.data || []
        setStudents(list)

        const sid = String(profile.id ?? studentIdFromRoute)
        setSelectedStudent(sid)
        const matched = list.find((st) => String(st.id) === sid)
        setSelectedStudentName(matched?.name || profile.name || '')

        if (examIdFromRoute != null && String(examIdFromRoute).trim() !== '') {
          setSelectedExam(String(examIdFromRoute).trim())
        } else {
          setSelectedExam('')
        }
      } catch (err) {
        if (!cancelled) {
          setErrorCard(err?.response?.data?.message || 'Could not load student from URL.')
        }
      }
    }

    applyRouteParams()
    return () => {
      cancelled = true
    }
  }, [
    isBaseAdmitcardPath,
    studentIdFromRoute,
    examIdFromRoute,
    courses.length,
    authToken,
    token,
    institute_id,
    location.pathname,
    location.key,
  ])

  const handleDownloadPdf = () => {
    if (!admitCardRef.current) return
    const opt = {
      margin: [8, 8, 8, 8],
      filename: `Admit-Card-${student?.name || selectedStudent || 'student'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, allowTaint: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] },
    }
    html2pdf().set(opt).from(admitCardRef.current).save()
  }

  const handlePrint = () => {
    window.print()
  }

  const showCard = Boolean(
    selectedCourse && selectedClass && selectedExam && selectedStudent && student,
  )
  const hasStudentDetails = Boolean(selectedStudent && student)
  const awaitingStudent =
    Boolean(selectedCourse && selectedClass && selectedExam) && !selectedStudent
  const loadingAdmitCard =
    Boolean(selectedCourse && selectedClass && selectedExam && selectedStudent) &&
    (loadingCard || (selectedExam && loadingSchedules))

  return (
    <>
      <style>
        {`
          .filter-card-container { overflow: visible !important; position: relative !important; z-index: 1 !important; }
          .filter-card-container .card { overflow: visible !important; position: relative !important; }
          .filter-card-container .card-body { overflow: visible !important; position: relative !important; }
          .rbt-menu { z-index: 9999 !important; position: absolute !important; max-height: 300px !important; overflow-y: auto !important; }
          .rbt-menu.dropdown-menu { z-index: 9999 !important; position: absolute !important; }
          .admit-top-header {
            margin-top: -12px;
            border-bottom: 1px solid #d1d3d4;
            background: #f3f4f6;
            padding: 12px 28px;
            width: 100%;
          }
          .admit-top-header-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
          }
          .admit-title-wrap {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .admit-header-logo {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            overflow: hidden;
            background: #223843;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .admit-page { max-width: 920px; margin: 24px auto; padding: 0 16px; }
          .print-admit-card-btn {
            transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          }
          .print-admit-card-btn:hover {
            background-color: #223843 !important;
            color: #ffffff !important;
            border-color: #223843 !important;
            box-shadow: 0 2px 8px rgba(34, 56, 67, 0.25);
          }
          @media (max-width: 768px) {
            .admit-top-header { margin-top: 55px; padding: 12px 16px; }
          }

          /* Print: full-width admit card; hide shell (works for Print button and Cmd/Ctrl+P). */
          @media print {
            @page { size: A4; margin: 10mm; }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              overflow: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body:has(.exam-sheet-admit-root) aside,
            body:has(.exam-sheet-admit-root) nav,
            body:has(.exam-sheet-admit-root) .navbar,
            body:has(.exam-sheet-admit-root) .sidebar,
            body:has(.exam-sheet-admit-root) [class*="sidebar"],
            body:has(.exam-sheet-admit-root) [class*="Sidenavbar"],
            body:has(.exam-sheet-admit-root) [class*="sidenavbar"],
            body:has(.exam-sheet-admit-root) .offcanvas,
            body:has(.exam-sheet-admit-root) .offcanvas-backdrop,
            body:has(.exam-sheet-admit-root) .no-print {
              display: none !important;
              visibility: hidden !important;
              width: 0 !important;
              max-width: 0 !important;
              height: 0 !important;
              overflow: hidden !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            body:has(.exam-sheet-admit-root) .App {
              display: block !important;
              overflow: visible !important;
              width: 100% !important;
              max-width: 100% !important;
            }
            body:has(.exam-sheet-admit-root) .app-main-content {
              display: block !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              flex: none !important;
              overflow: visible !important;
              min-width: 0 !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root {
              margin-left: 0 !important;
              margin-right: 0 !important;
              margin-top: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              overflow: visible !important;
              box-sizing: border-box !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .container-fluid,
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .container-fluid > div {
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              max-width: 100% !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .admit-top-header {
              display: none !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .admit-page {
              margin: 0 !important;
              padding: 0 8px !important;
              max-width: 100% !important;
              width: 100% !important;
              box-sizing: border-box !important;
              overflow: visible !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .admit-card-root {
              max-width: 100% !important;
              width: 100% !important;
              box-sizing: border-box !important;
              box-shadow: none !important;
              overflow: visible !important;
            }
            /* Keep exam-table inside the printable width so the Time column's right border is not clipped */
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table {
              width: 100% !important;
              max-width: 100% !important;
              table-layout: fixed !important;
              border-collapse: separate !important;
              border-spacing: 0 !important;
              box-sizing: border-box !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table th,
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table td {
              border: 1px solid #111827 !important;
              box-sizing: border-box !important;
              overflow-wrap: anywhere !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table th + th,
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table td + td {
              border-left: none !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table tbody tr td {
              border-top: none !important;
            }
            /* Sr. No. | Subject Name | Paper Type | Exam Date | Time */
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table th:nth-child(1),
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table td:nth-child(1) {
              width: 8% !important;
              text-align: center !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table th:nth-child(2),
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table td:nth-child(2) {
              width: 26% !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table th:nth-child(3),
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table td:nth-child(3) {
              width: 24% !important;
              white-space: normal !important;
              word-break: break-word !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table th:nth-child(4),
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table td:nth-child(4) {
              width: 18% !important;
              white-space: nowrap !important;
              overflow-wrap: normal !important;
            }
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table th:nth-child(5),
            body:has(.exam-sheet-admit-root) .exam-sheet-admit-root .exam-table td:nth-child(5) {
              width: 24% !important;
              white-space: normal !important;
              overflow-wrap: anywhere !important;
            }
          }
        `}
      </style>

      <div
        className="admit-main-wrap exam-sheet-admit-root"
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div className="container-fluid p-0">
          <div style={{ marginLeft: isMobile ? '0' : '-40px' }}>
            <div className="admit-top-header">
              <div className="admit-top-header-row">
                <div className="admit-title-wrap">
                  <div className="admit-header-logo">
                    {!logoError ? (
                      <img
                        src={headerLogoSrc}
                        alt="School Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                    )}
                  </div>
                  <h3
                    className="m-0 fw-bold"
                    style={{
                      letterSpacing: '-0.02em',
                      fontSize: 'clamp(18px, 2.5vw, 22px)',
                      color: '#223843',
                      lineHeight: '1.2',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Print Admit Card
                    {selectedStudentName ? ` — ${selectedStudentName}` : ''}
                  </h3>
                </div>
                {!isMobile && <ProfileDropdown logout={logout} />}
              </div>
            </div>
          </div>
        </div>

        <div className="no-print">
          <Container fluid className="mt-4 px-3 px-md-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <button
                type="button"
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                title={showFilterCard ? 'Hide filters' : 'Show filters'}
              >
                <BsFunnelFill size={20} />
              </button>
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={() => navigate('/ExamSheet')}>
                  <FaArrowLeft className="me-1" />
                  Exam sheet
                </Button>
                {isAdminUser && (
                  <Button
                    onClick={() => navigate('/ExamSheet/BulkAdmitcard')}
                    style={{
                      backgroundColor: '#223843',
                      color: '#ffffff',
                      borderColor: '#223843',
                      fontWeight: 600,
                    }}
                  >
                    <FaPrint className="me-1" />
                    Print Bulk Admit Cards
                  </Button>
                )}
                {showCard && hasStudentDetails && !(selectedExam && loadingSchedules) && (
                  <>
                    <Button
                      variant="outline-secondary"
                      className="print-admit-card-btn"
                      onClick={handlePrint}
                    >
                      <FaPrint className="me-1" />
                      Print Admit Card
                    </Button>
                    <Button
                      onClick={handleDownloadPdf}
                      style={{
                        backgroundColor: '#223843',
                        color: '#ffffff',
                        borderColor: '#223843',
                        fontWeight: 600,
                      }}
                    >
                      <FaDownload className="me-1" />
                      Download Admit Card
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div
              className="filter-card-container"
              style={{
                maxHeight: showFilterCard ? '1200px' : '0',
                overflow: showFilterCard ? 'visible' : 'hidden',
                transition: 'max-height 0.3s ease-in-out',
                marginBottom: showFilterCard ? '24px' : '0',
              }}
            >
              {showFilterCard && (
                <Card
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    overflow: 'visible',
                  }}
                >
                  <div
                    style={{
                      padding: '20px',
                      borderBottom: '1px solid #F3F4F6',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BsSearch style={{ color: '#fff', fontSize: '18px' }} />
                        </div>
                        <div className="text-start">
                          <h5 className="m-0 fw-semibold" style={{ color: '#111827', fontSize: '16px' }}>
                            Search admit card
                          </h5>
                          <p className="m-0 mt-1 text-muted small">
                            Select course, class, exam, and student. The admit card opens as soon as
                            you select the student.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                        title="Reset"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                  <Card.Body className="p-4" style={{ overflow: 'visible' }}>
                    <Row className="g-4 text-start">
                      <Col xs={12} md={4}>
                        <label className="fw-semibold mb-2 d-block small text-muted">Course</label>
                        <Typeahead
                          id="admit-course"
                          labelKey="name"
                          onChange={(selected) => {
                            const c = selected[0]
                            handleCourseChange(c ? c.id : '')
                          }}
                          options={courses}
                          placeholder="Select course"
                          selected={courses.filter((cs) => String(cs.id) === String(selectedCourse))}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                            },
                          }}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <label className="fw-semibold mb-2 d-block small text-muted">Class</label>
                        <Typeahead
                          id="admit-class"
                          labelKey="class_name"
                          onChange={(selected) => {
                            const c = selected[0]
                            handleClassChange(c ? c.id : '')
                          }}
                          options={classes}
                          placeholder="Select class"
                          selected={classes.filter((cs) => String(cs.id) === String(selectedClass))}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                            },
                          }}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <label className="fw-semibold mb-2 d-block small text-muted">Exam</label>
                        <Typeahead
                          id="admit-exam"
                          labelKey={(opt) => opt.exam_name || 'Exam'}
                          onChange={(selected) => {
                            const ex = selected[0]
                            handleExamChange(ex ? ex.id : '')
                          }}
                          options={examOptions}
                          placeholder={
                            !selectedCourse
                              ? 'Select a course first'
                              : !selectedClass
                                ? 'Select a class first'
                                : examOptions.length === 0
                                  ? 'No exams for this course/class'
                                  : 'Select exam'
                          }
                          disabled={!selectedCourse || !selectedClass}
                          selected={examOptions.filter((e) => String(e.id) === String(selectedExam))}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                            },
                          }}
                        />
                        {selectedExam && loadingSchedules && (
                          <div className="small text-muted mt-2">Loading subject schedule…</div>
                        )}
                      </Col>
                    </Row>
                    <Row className="g-4 text-start mt-1">
                      <Col xs={12} md={8}>
                        <label className="fw-semibold mb-2 d-block small text-muted">
                          Student
                        </label>
                        <Typeahead
                          id="admit-student"
                          labelKey="name"
                          onChange={(selected) => {
                            const s = selected[0]
                            handleStudentChange(s ? s.id : '')
                          }}
                          options={students}
                          placeholder={
                            !selectedClass
                              ? 'Select a class first'
                              : !selectedExam
                                ? 'Select an exam first'
                                : 'Select student to open admit card'
                          }
                          disabled={!selectedClass || !selectedExam}
                          selected={
                            selectedStudent
                              ? students.filter((st) => String(st.id) === String(selectedStudent))
                              : []
                          }
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                            },
                          }}
                        />
                        {awaitingStudent && (
                          <div className="small text-muted mt-2">
                            Select a student to open the admit card.
                          </div>
                        )}
                        {loadingCard && (
                          <div className="d-flex align-items-center gap-2 small text-muted mt-2">
                            <Spinner animation="border" size="sm" />
                            Opening admit card…
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </div>

            {errorCard && !loadingCard && (
              <div
                className="mb-4 p-3 rounded"
                style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b' }}
              >
                {errorCard}
              </div>
            )}
          </Container>
        </div>

        <div className="admit-page">
          {loadingAdmitCard && (
            <div className="d-flex flex-column justify-content-center align-items-center gap-2 py-5 text-muted">
              <Spinner animation="border" variant="primary" />
              <span>
                {loadingCard
                  ? 'Opening admit card…'
                  : 'Loading exam subject schedule…'}
              </span>
            </div>
          )}
          {showCard && !loadingAdmitCard && (
            useShriHitTemplate ? (
              <AdmitCardShriHit1
                admitCardRef={admitCardRef}
                student={student}
                courseName={courseName}
                className={className}
                instituteName={instituteName}
                examName={examName}
                sessionName={sessionName}
                instituteLogoSrc={instituteLogoSrc}
                examRows={examRows}
                examLocation={resolvedExamLocation}
              />
            ) : (
              <AdmitCardPrintView
                admitCardRef={admitCardRef}
                student={student}
                courseName={courseName}
                className={className}
                instituteName={instituteName}
                examName={examName}
                sessionName={sessionName}
                instituteLogoSrc={instituteLogoSrc}
                examRows={examRows}
                examLocation={resolvedExamLocation}
              />
            )
          )}

          {!showCard && !loadingAdmitCard && awaitingStudent && (
            <p className="text-muted text-center py-4">
              Select a student to open the admit card.
            </p>
          )}
          {!showCard && !loadingAdmitCard && selectedCourse && selectedClass && !selectedExam && (
            <p className="text-muted text-center py-4">Select an exam, then a student to open the admit card.</p>
          )}
          {!showCard && !loadingAdmitCard && selectedCourse && !selectedClass && (
            <p className="text-muted text-center py-4">Select a class, exam, and student to open the admit card.</p>
          )}
        </div>
      </div>
    </>
  )
}
